import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import React, { useCallback, useMemo, useState } from 'react';
import { SwitchToTemplatingItemProvider } from '../contexts/templatingItemSwitch';
import { useMoveTemplatingItem } from '../hooks/useMoveTemplatingItem';
import { useTemplatingItemSummaries } from '../hooks/useTemplatingItemSummaries';
import {
  CompositeReportDefinitionSummary,
  Folder,
  TemplateSummary,
  TemplatingItemSummary,
} from '../models';
import { TemplatingItemNode } from './FileSystemNode';
import { RootFileSystemTree } from './FileSystemTree';

const FileSystemComponent: React.FC<{
  folderNodeActions?: (data: Folder) => React.ReactNode;
  templateNodeActions?: (data: TemplateSummary) => React.ReactNode;
  compositeReportDefinitionNodeActions?: (
    data: CompositeReportDefinitionSummary,
  ) => React.ReactNode;
}> = ({
  folderNodeActions,
  templateNodeActions,
  compositeReportDefinitionNodeActions,
}) => {
  const { data: templatingItemSummaryPages } = useTemplatingItemSummaries();
  const [draggingItem, setDraggingItem] =
    useState<TemplatingItemSummary | null>(null);
  const { moveTemplatingItem } = useMoveTemplatingItem();

  const templatingItemSummaries = useMemo(() => {
    return templatingItemSummaryPages?.pages.flatMap(_ => _);
  }, [templatingItemSummaryPages]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggingItem(event.active.data.current?.templatingItem ?? null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingItem(null);
      if (!event.over) {
        return;
      }
      const templatingItem = event.active.data.current
        ?.templatingItem as TemplatingItemSummary;
      const folder = event.over.data.current?.folder as Folder | undefined;
      if (templatingItem) {
        moveTemplatingItem(templatingItem, folder);
      }
    },
    [moveTemplatingItem],
  );

  return (
    <SwitchToTemplatingItemProvider>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        {templatingItemSummaries && (
          <RootFileSystemTree
            templatingItemSummaries={templatingItemSummaries}
            folderNodeActions={folderNodeActions}
            templateNodeActions={templateNodeActions}
            compositeReportDefinitionNodeActions={
              compositeReportDefinitionNodeActions
            }
          />
        )}
        <DragOverlay>
          {draggingItem ? (
            <TemplatingItemNode templatingItem={draggingItem} />
          ) : null}
        </DragOverlay>
      </DndContext>
    </SwitchToTemplatingItemProvider>
  );
};

export { FileSystemComponent };
